import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useFlow } from "@/contexts/FlowContext";
import { useModel } from "@/contexts/ModelContext";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useCurrentMedia, useSyncPromptLabels, useSetPendingAiTool } from "@/store";
import { AiTools } from "@/utils/api/datasets";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { GeneratePredictionsBtn } from "./GeneratePredictionsBtn";
import { TextPrompt } from "./TextPrompt";
import { TextPromptProposalChips } from "./TextPromptProposalChips";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { getAutoInvokeStatus, setAutoInvokeStatus } from "@/utils/autoInvokeStatus";
import _ from "lodash";
import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { useAutoPredictionsTrigger } from "@/contexts/AutoPredictionsContext";
import { deleteAnnotationsByLabelCompletely } from "../PromptLabel/Cards/cardUtils";
import { useInferenceStrategy } from "@/strategies/inference";
import { useRunInference } from "@/hooks/useRunInference";
import { MediaItemStatus } from "@/types/media";
import { useFlowState } from "@/hooks/useFlowState";
import { DeploySteps } from "@/types/flows";

interface GeneratePredictionsProps {
    disabled?: boolean;
}

export const GeneratePredictions = ({ disabled = false }: GeneratePredictionsProps) => {
    const { setFlowLocked, setCurrentFrameAnnotations } = useFlow();
    const { getPredictionsForFile } = useGetPredictions();
    const currentMedia = useCurrentMedia();
    const {
        setAnnotationsForFileAndFrameId,
        annotationsForFileAndFrameId,
        textPrompts,
        promptLabels,
        setTextPromptStr,
        updatePromptLabels
    } = useModel();
    const { currentFramePredictions, currentFrameAnnotations } = useComputedValues();
    const syncPromptLabels = useSyncPromptLabels();
    const { track } = useAnalytics();
    const { registerTrigger } = useAutoPredictionsTrigger();
    const setPendingAiTool = useSetPendingAiTool();
    const { runInference } = useRunInference();

    // Get the inference strategy for the current flow (Build vs Improve)
    const inferenceStrategy = useInferenceStrategy();
    const { step } = useFlowState();

    // Track which file we've auto-invoked for to prevent duplicate auto-invokes
    // but allow auto-invoke when switching to a new file
    const autoInvokedForFileRef = useRef<string | null>(null);

    // Check if there are any drawn annotations
    const hasDrawnAnnotations = currentFrameAnnotations.length > 0;

    // Check if there are any draft annotations
    const hasDraftAnnotations = currentFrameAnnotations.some((anno) => anno.draft);

    // Check if there are object cards
    const hasObjectCards = promptLabels.size > 0;

    // Text prompt handler - uses strategy pattern to determine inference behavior
    const handleGeneratePredictions = useCallback(async () => {
        setFlowLocked(true);

        // Identify NEW text prompts (not already in promptLabels)
        const existingLabelNames = new Set(Array.from(promptLabels.keys()));
        const newTextPrompts = textPrompts.filter((prompt) => !existingLabelNames.has(prompt));
        const hasExistingLabels = promptLabels.size > 0;

        // Combined prompts for syncing
        const combinedTextPrompts = _.uniq([
            ...textPrompts,
            ...Array.from(promptLabels.values()).map((label) => label.name)
        ]);

        // Check if we need to delete a label after predictions (from NoResultsEmptyState chip click)
        const labelToDelete = sessionStorage.getItem("labelToDeleteAfterPredictions");

        syncPromptLabels(combinedTextPrompts);

        try {
            await new Promise<void>(async (resolve, reject) => {
                if (!currentMedia?.file.metadata?.naturalDimensions || !currentMedia?.frame) {
                    resolve();
                    return;
                }

                const mediaItemToUse = currentMedia?.file.isVideo
                    ? currentMedia?.frame!
                    : currentMedia?.file!;
                const extractedFrameMeta = currentMedia?.file.isVideo
                    ? {
                          videoId: currentMedia?.file.id,
                          segment: currentMedia?.frame?.metadata?.segment!
                      }
                    : undefined;

                try {
                    // If we have existing labels and new text prompts, run predictions only for new classes
                    // This appends to existing annotations instead of replacing them
                    if (hasExistingLabels && newTextPrompts.length > 0) {
                        // Set pending AI tool for annotation sync (SAM3 = text prompting)
                        console.log(
                            "[GeneratePredictions] Setting pending AI tool: TEXT_PROMPTING (new labels branch)"
                        );
                        setPendingAiTool(AiTools.TEXT_PROMPTING);

                        // Run predictions for each new label in single-label mode (appends)
                        for (const newLabel of newTextPrompts) {
                            if (currentMedia?.file.isVideo) {
                                if (typeof currentMedia?.frame?.metadata?.segment !== "number") {
                                    continue;
                                }
                            }

                            await getPredictionsForFile({
                                mediaItem: mediaItemToUse,
                                extractedFrameMeta,
                                inferenceType: InferenceTypes.SAM3,
                                textPrompts: [newLabel],
                                isDraft: true,
                                targetLabel: newLabel
                            });
                        }
                        resolve();
                    } else {
                        // Full prediction for all prompts - use mixed inference based on hasDiffs
                        if (
                            currentMedia?.file.isVideo &&
                            typeof currentMedia?.frame?.metadata?.segment !== "number"
                        ) {
                            resolve();
                            return;
                        }

                        // Get labels for each inference type using the strategy
                        const sam3Labels = inferenceStrategy.getSam3Labels(
                            promptLabels,
                            textPrompts
                        );
                        const owlv2Labels = inferenceStrategy.getOwlv2Labels(promptLabels);

                        // Debug log - include full promptLabels state
                        console.log("[GeneratePredictions] Inference strategy:", {
                            sam3Labels,
                            owlv2Labels,
                            hasDiffsRelevant: inferenceStrategy.hasDiffsRelevant(),
                            promptLabelsDetail: Array.from(promptLabels.entries()).map(
                                ([name, label]) => ({ name, hasDiffs: label.hasDiffs })
                            )
                        });

                        // Set pending AI tool for annotation sync
                        const willRunBoxPrompting = owlv2Labels.length > 0;
                        const willRunTextPrompting = sam3Labels.length > 0;

                        if (willRunBoxPrompting && willRunTextPrompting) {
                            // Mixed inference - prefer BOX_PROMPTING since user has actively edited those labels
                            setPendingAiTool(AiTools.BOX_PROMPTING);
                        } else if (willRunBoxPrompting) {
                            setPendingAiTool(AiTools.BOX_PROMPTING);
                        } else if (willRunTextPrompting) {
                            setPendingAiTool(AiTools.TEXT_PROMPTING);
                        }

                        // Use centralized inference hook
                        await runInference({
                            mediaItem: mediaItemToUse,
                            extractedFrameMeta,
                            annotationsForFileAndFrameId,
                            isDraft: true,
                            sam3Labels,
                            owlv2Labels,
                            sam3ConfidenceThreshold:
                                inferenceStrategy.getTextPromptConfidenceThreshold()
                        });
                    }
                    setTextPromptStr("");
                    // No approve phase, so unlock immediately
                    setFlowLocked(false);

                    // Delete the source label if one was stored (from NoResultsEmptyState chip click)
                    if (labelToDelete) {
                        // Delete all annotations for this label across all files
                        setAnnotationsForFileAndFrameId((prev) => {
                            return deleteAnnotationsByLabelCompletely(prev, labelToDelete);
                        });

                        // Calculate remaining label names
                        const remainingLabelNames = Array.from(promptLabels.keys()).filter(
                            (labelName) => labelName !== labelToDelete
                        );

                        // Update prompt labels to remove the deleted label
                        updatePromptLabels([], [labelToDelete]);

                        // Sync prompt labels to ensure consistency
                        setTimeout(() => {
                            syncPromptLabels(remainingLabelNames);
                        }, 0);

                        // Force a canvas redraw
                        setTimeout(() => {
                            setCurrentFrameAnnotations((prev: any) => {
                                return [...prev];
                            });
                        }, 50);

                        // Clear the stored label
                        sessionStorage.removeItem("labelToDeleteAfterPredictions");
                    }

                    resolve();
                } catch (error) {
                    reject(error);
                }
            });
            // on success, keep locked (PromptSidebarContent will manage unlocking based on predictions)
        } catch (e) {
            console.error(e);
            // On error, unlock so user can retry
            setFlowLocked(false);
            throw e;
        }
    }, [
        currentMedia,
        getPredictionsForFile,
        textPrompts,
        setFlowLocked,
        syncPromptLabels,
        setAnnotationsForFileAndFrameId,
        annotationsForFileAndFrameId,
        promptLabels,
        setTextPromptStr,
        updatePromptLabels,
        setCurrentFrameAnnotations,
        setPendingAiTool,
        inferenceStrategy,
        runInference
    ]);

    const handleSmartClickSwitcher = useCallback(async () => {
        if (textPrompts.length > 0) {
            // If there are text prompts, use text prompt generation
            await handleGeneratePredictions();
        } else if (hasObjectCards) {
            // If there are object cards but no text prompts, use text prompt generation with existing labels
            await handleGeneratePredictions();
        }
    }, [handleGeneratePredictions, hasObjectCards, textPrompts.length]);

    const {
        error: errorGeneratePredictions,
        reset: resetGeneratePredictions,
        invoke: invokeGeneratePredictions
    } = useAsyncBoundOperation(handleSmartClickSwitcher);

    // Combine loading states - either manual generation or auto-generation
    const isGeneratingPredictions = useIsGeneratingPredictions();

    // Auto-invoke predictions when entering a new file with no predictions.
    // This only applies to the Build flow's Prompt step. In the Improve flow the
    // images already have (or intentionally lack) annotations, so auto-generating
    // predictions would be disruptive — the user should trigger it manually if needed.
    useEffect(() => {
        // Never auto-invoke on the improve page — it is a review/editing surface,
        // not a prediction-generation surface.
        if (step === DeploySteps.IMPROVE) {
            return;
        }

        const currentFileId = currentMedia?.file?.id;

        // Auto-invoke if there are text prompts OR existing object cards (promptLabels)
        const hasPromptsOrLabels = textPrompts.length > 0 || hasObjectCards;

        // Check if we've already auto-invoked for this specific file
        const hasAutoInvokedForThisFile = autoInvokedForFileRef.current === currentFileId;

        // Check if the file (and frame for videos) has finished uploading
        const fileIsUploaded = currentMedia?.file?.status === MediaItemStatus.COMPLETED;
        const frameIsUploaded =
            !currentMedia?.file?.isVideo ||
            currentMedia?.frame?.status === MediaItemStatus.COMPLETED;
        const isUploaded = fileIsUploaded && frameIsUploaded;

        const shouldAutoInvoke =
            currentFileId &&
            !hasAutoInvokedForThisFile &&
            hasPromptsOrLabels &&
            isUploaded && // Wait for file to be uploaded
            currentFramePredictions.length === 0 &&
            !isGeneratingPredictions &&
            !hasDrawnAnnotations &&
            !hasDraftAnnotations && // Don't auto-invoke if there are draft annotations (predictions were already generated)
            currentMedia?.file.metadata?.naturalDimensions &&
            currentMedia?.frame;

        if (shouldAutoInvoke) {
            autoInvokedForFileRef.current = currentFileId;
            setAutoInvokeStatus(true); // Keep global flag for backwards compatibility
            invokeGeneratePredictions();
        }
    }, [
        step,
        textPrompts.length,
        hasObjectCards,
        currentFramePredictions.length,
        isGeneratingPredictions,
        hasDrawnAnnotations,
        hasDraftAnnotations,
        currentMedia?.file?.id,
        currentMedia?.file?.status,
        currentMedia?.file?.isVideo,
        currentMedia?.frame?.status,
        currentMedia?.file.metadata?.naturalDimensions,
        currentMedia?.frame,
        invokeGeneratePredictions
    ]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            // Track generate predictions button click
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "button_click",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
            e.preventDefault();
        },
        [
            invokeGeneratePredictions,
            track,
            hasDrawnAnnotations,
            textPrompts,
            hasObjectCards,
            currentFrameAnnotations.length
        ]
    );

    // Form submit handler
    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            // Track generate predictions form submit
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "form_submit",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
            e.preventDefault();
        },
        [
            invokeGeneratePredictions,
            track,
            hasDrawnAnnotations,
            textPrompts,
            hasObjectCards,
            currentFrameAnnotations.length
        ]
    );

    const handleRetry = useCallback(
        (e: React.MouseEvent) => {
            resetGeneratePredictions();
            invokeGeneratePredictions();
            e.preventDefault();
        },
        [invokeGeneratePredictions, resetGeneratePredictions]
    );

    // Keyboard shortcut: Enter to generate predictions
    useKeyboardShortcut({
        key: "Enter",
        handler: () => {
            // Track generate predictions keyboard shortcut
            track({
                event: AnalyticsFlowEvent.GENERATE_PREDICTIONS_BUTTON_CLICKED,
                data: {
                    triggerMethod: "keyboard_shortcut",
                    hasDrawnAnnotations: hasDrawnAnnotations,
                    hasTextPrompts: textPrompts.length > 0,
                    hasObjectCards: hasObjectCards,
                    textPrompts: textPrompts,
                    annotationCount: currentFrameAnnotations.length
                }
            });

            invokeGeneratePredictions();
        },
        requireMeta: false
    });

    useEffect(() => {
        registerTrigger(invokeGeneratePredictions);
    }, [registerTrigger, invokeGeneratePredictions]);

    // Determine if button should be disabled
    // Button is only disabled during generation or if there's nothing to generate
    const isDisabled =
        isGeneratingPredictions || disabled || (!textPrompts.length && !hasObjectCards);
    // Note: We allow generating predictions at any time - new text prompts will append to existing annotations

    const showTextPromptProposalChips = !hasObjectCards;

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="prompt-container">
                <TextPrompt />
                <GeneratePredictionsBtn disabled={isDisabled} onClick={handleClick} />
                {errorGeneratePredictions && (
                    <ErrorInfo error={errorGeneratePredictions} onRetryClick={handleRetry} />
                )}
            </div>
            {showTextPromptProposalChips && (
                <TextPromptProposalChips
                    isDisabled={isGeneratingPredictions}
                    showPoweredBy={false}
                />
            )}
        </form>
    );
};
